<template>
  <label :class="className">
    <input v-model="model" class="v-switch__input" :disabled type="checkbox" />

    <span class="v-switch__core">
      <span class="v-switch__knob" />
    </span>
    <span v-if="label || $slots.default" class="v-switch__label">
      <slot>{{ label }}</slot>
    </span>

    <VIconHelp v-if="help" :help style="margin-left: 5px" type="placeholder" />
  </label>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useClassName } from '../composables/useClassName.ts'
import VIconHelp from './VIconHelp.vue'

interface Props {
  /**
   * The text which is displayed next to the switch.
   */
  label?: string

  /**
   * Disables the switch.
   */
  disabled?: boolean

  /**
   * Displays one or more help messages in a tooltip by hovering over a help icon after the switch label.
   */
  help?: string | string[]
}

const { disabled, label = '', help = '' } = defineProps<Props>()

/**
 * Indicates if the switch should be on or off.
 */
const model = defineModel<boolean>({ default: false })

defineSlots<{
  default?(): any
}>()

const className = useClassName(
  'v-switch',
  computed(() => [label]),
  computed(() => ({ 'is-disabled': disabled, 'is-checked': model.value })),
)
</script>

<style>
.v-switch {
  display: inline-flex;
  align-items: center;
  position: relative;
  font-size: 14px;
  line-height: 16px;
  vertical-align: middle;
  cursor: pointer;

  &.is-checked .v-switch__core {
    border-color: var(--color-primary);
    background-color: var(--color-primary);

    .v-switch__knob {
      left: calc(100% - 12px);
    }
  }

  &.is-disabled {
    opacity: 0.5;
    cursor: default;
  }

  &:not(.is-disabled):hover {
    .v-switch__core {
      background-color: var(--color-grey-20);
    }

    &.is-checked .v-switch__core {
      border-color: var(--color-secondary);
      background-color: var(--color-secondary);
    }
  }
}

.v-switch__core {
  display: inline-block;
  position: relative;
  width: 30px;
  height: 16px;
  border: 2px solid var(--color-grey-12);
  outline: none;
  border-radius: 8px;
  background: var(--color-grey-12);
  transition:
    border-color 0.3s,
    background-color 0.3s;
  vertical-align: middle;
  flex-shrink: 0;
}

.v-switch__knob {
  position: absolute;
  width: 12px;
  height: 12px;
  top: 0;
  left: 0;
  border-radius: 100%;
  transition: all 0.3s;
  background-color: #fff;

  &:hover {
    box-shadow: 0 0 6px 0 rgb(0, 0, 0, 0.1);
  }
}

.v-switch__input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  margin: 0;
}

.v-switch__label {
  margin-left: 8px;
}
</style>
